// pages/WebsiteLinks/ListArchivedData.js
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import DataTable from 'react-data-table-component';
import backendURL from "../../config";
import { Tooltip } from '@mui/material';

export default function ListArchivedData({ refresh, setRefresh }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isRetraining, setIsRetraining] = useState(false);

  const fetchDocuments = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${backendURL}/get-archived-website-links`, { headers });
      const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data || [];
      setDocuments(responseData);
      setFilteredDocuments(responseData);
    } catch (err) {
      setError(`Error fetching archived data: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, [refresh, fetchDocuments]);

  useEffect(() => {
    const lowerCaseSearchValue = searchText.toLowerCase();
    const filtered = documents.filter(doc =>
      doc.website_link?.toLowerCase().includes(lowerCaseSearchValue) ||
      doc.title_tag?.toLowerCase().includes(lowerCaseSearchValue)
    );
    setFilteredDocuments(filtered);
  }, [searchText, documents]);

  const handleRetrain = async (ids) => {
    setIsRetraining(true);
    setError(null);
    try {
        const token = localStorage.getItem('access_token');
        const headers = { Authorization: `Bearer ${token}` };
        const response = await axios.post(`${backendURL}/retrain-archived-data`, { link_ids: ids }, { headers });
        setError(response.data.message || 'Retraining initiated successfully.');
        setSelectedRows([]);
        fetchDocuments(); // Refresh data
        setRefresh(prev => !prev); // Trigger refresh in parent Scraper component
    } catch (err) {
        setError(`Error during retraining: ${err.response?.data?.message || err.message}`);
    } finally {
        setIsRetraining(false);
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm(`PERMANENTLY DELETE item ID ${documentId}? This cannot be undone.`)) return;
    try {
      const token = localStorage.getItem('access_token');
      setError(null);
      await axios.delete(`${backendURL}/delete-website-link/${documentId}`, { headers: { Authorization: `Bearer ${token}` } });
      setError("Item deleted permanently.");
      fetchDocuments();
    } catch (err) {
      setError(`Error deleting item: ${err.response?.data?.message || err.message}`);
    }
  };

  const columns = [
    {
      name: 'URL',
      selector: row => row.website_link,
      sortable: true,
      maxWidth: '400px',
      cell: row => (<Tooltip title={row.website_link} arrow><span className="ellipsis-text">{row.website_link}</span></Tooltip>)
    },
    {
      name: 'Page Name',
      selector: row => row.title_tag,
      sortable: true,
      maxWidth: '400px',
      cell: row => (<Tooltip title={row.title_tag} arrow><span className="ellipsis-text">{row.title_tag || '(No Title)'}</span></Tooltip>)
    },
    {
      name: 'Date Archived',
      selector: row => row.updated_date,
      sortable: true,
      format: row => row.updated_date ? new Date(row.updated_date).toLocaleString() : 'N/A'
    },
    {
      name: 'Actions',
      width: '150px',
      cell: row => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around', width: '100%' }}>
          <Tooltip title="Retrain & Unarchive" arrow>
            <button className="RefreshIcon" onClick={() => handleRetrain([row.id])} disabled={isRetraining}>
              <i className="fa fa-history"></i>
            </button>
          </Tooltip>
          <Tooltip title="Delete Permanently" arrow>
            <button className="deleteIcon" onClick={() => handleDelete(row.id)}>
              <i className="fa fa-trash-o"></i>
            </button>
          </Tooltip>
        </div>
      ),
      ignoreRowClick: true,
      button: true,
    },
  ];

  return (
    <div className="row">
      <div className="col-12">
        <div className="card flex-fill">
          <div className="card-header">
            <h5 className="card-title mb-0">Archived Data</h5>
            <p className="card-subtitle text-muted">Items here are not in the active training set. Retrain them to move them back to "Trained Data".</p>
          </div>
          <div className="card-body">
            <div className="row mb-3 align-items-center">
              <div className="col-lg-7 col-md-6 mb-2 mb-md-0 d-flex flex-wrap gap-2">
                {selectedRows.length > 0 && (
                  <button
                    className="btn btn-primary"
                    onClick={() => handleRetrain(selectedRows.map(r => r.id))}
                    disabled={isRetraining}
                  >
                    {isRetraining ? (
                      <><i className="fa fa-spinner fa-spin me-1"></i> Retraining...</>
                    ) : (
                      <><i className="fa fa-history me-1"></i> Bulk Retrain ({selectedRows.length})</>
                    )}
                  </button>
                )}
              </div>
              <div className="col-lg-5 col-md-6">
                <div className="input-group">
                  <input type="text" className="form-control" placeholder="Search archived URL, Title..." value={searchText} onChange={(e) => setSearchText(e.target.value)} />
                </div>
              </div>
            </div>

            {error && <div className="alert alert-info">{error}</div>}

            <div className="table-responsive">
              {loading ? (
                <div className="text-center p-5"><i className="fa fa-spinner fa-spin fa-3x"></i></div>
              ) : (
                <DataTable
                  columns={columns}
                  data={filteredDocuments}
                  pagination
                  selectableRows
                  onSelectedRowsChange={({ selectedRows }) => setSelectedRows(selectedRows)}
                  highlightOnHover
                  pointerOnHover
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}